import { useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { whatsappHref } from "@/lib/contact";

const fmtINR = (v) =>
  v >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : v >= 1e5 ? `₹${(v / 1e5).toFixed(1)} L` : `₹${Math.round(v).toLocaleString("en-IN")}`;

const PRICE_CHIPS = [
  { label: "₹40 L", value: 4000000 },
  { label: "₹60 L", value: 6000000 },
  { label: "₹1 Cr", value: 10000000 },
];

const PRESETS = [
  { id: "resale-office", label: "Ready Office", hint: "Resale · Block A", purchase: 6400000, fitout: 500000, rent: 45000 },
  { id: "resale-retail", label: "Ready Retail", hint: "Resale · Ground Floor", purchase: 7200000, fitout: 0, rent: 40000 },
  { id: "compact-suite", label: "Compact Suite", hint: "₹40 L entry", purchase: 4000000, fitout: 300000, rent: 28000 },
];

const Slider = ({ label, value, min, max, step, onChange, format, testId }) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF]">{label}</label>
        <span data-testid={`${testId}-value`} className="font-mono text-sm text-[#F3E5AB]">{format(value)}</span>
      </div>
      <input
        type="range"
        data-testid={testId}
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--fill": `${pct}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full"
      />
    </div>
  );
};

export default function Calculator() {
  const [purchase, setPurchase] = useState(4000000);
  const [fitout, setFitout] = useState(500000);
  const [rent, setRent] = useState(35000);
  const [tenure, setTenure] = useState(5);
  const [appreciation, setAppreciation] = useState(4);

  const total = purchase + fitout;
  const annualRent = rent * 12;
  const grossYield = (annualRent * 100) / total;
  const projectedValue = total * Math.pow(1 + appreciation / 100, tenure);
  const totalRent = annualRent * tenure;
  const roi = ((totalRent + (projectedValue - total)) * 100) / total;

  const waHref = whatsappHref(
    `Hi Ashish, I used the EON investment calculator.\nTotal investment: ~${fmtINR(total)}\nExpected rent: ~${fmtINR(rent)}/month\nIndicative gross yield: ~${grossYield.toFixed(1)}%\nIndicative ROI over ${tenure} yrs: ~${roi.toFixed(0)}%\nPlease WhatsApp me the current EON options.`
  );

  const metrics = [
    { label: "Total Investment", value: fmtINR(total), testId: "calc-total-investment" },
    { label: "Annual Rent", value: fmtINR(annualRent), testId: "calc-annual-rent" },
    { label: "Indicative Gross Yield", value: `${grossYield.toFixed(1)}%`, testId: "calc-gross-yield" },
    { label: `Projected Value · ${tenure} yrs`, value: fmtINR(projectedValue), testId: "calc-projected-value" },
    { label: `Total ROI · ${tenure} yrs`, value: `${roi.toFixed(0)}%`, testId: "calc-total-roi" },
  ];

  return (
    <section id="calculator" data-testid="calculator-section" className="py-20 sm:py-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Investment Calculator</p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#F3F4F6] max-w-3xl">
            What could your EON <span className="gold-text italic">investment look like?</span>
          </h2>
        </motion.div>

        <div
          data-testid="calculator-panel"
          className="mt-10 grid lg:grid-cols-2 items-start gap-5"
        >
          <div className="rounded-2xl border border-white/[0.07] bg-[#121418] p-8 sm:p-10 flex flex-col gap-9">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">Ready-to-Move Scenarios</div>
              <div className="grid grid-cols-3 gap-2" data-testid="calc-presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`calc-preset-${p.id}`}
                    onClick={() => { setPurchase(p.purchase); setFitout(p.fitout); setRent(p.rent); }}
                    className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] px-3 py-3 text-left hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 hover:shadow-[0_0_20px_rgba(212,175,55,0.15)] transition-all duration-300"
                  >
                    <span className="block font-sans text-xs font-semibold text-[#F3F4F6]">{p.label}</span>
                    <span className="block mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[#9CA3AF]">{p.hint}</span>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9CA3AF] mb-3">Purchase Price</div>
              <div className="flex flex-wrap gap-2 mb-5" data-testid="calc-price-chips">
                {PRICE_CHIPS.map((c) => (
                  <button
                    key={c.label}
                    data-testid={`calc-chip-${c.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                    onClick={() => setPurchase(c.value)}
                    className={`px-5 py-2.5 rounded-full font-mono text-xs tracking-[0.1em] transition-all duration-300 ${
                      purchase === c.value
                        ? "bg-[#D4AF37] text-[#0B0C0E] shadow-[0_0_20px_rgba(212,175,55,0.35)]"
                        : "border border-white/10 text-[#9CA3AF] hover:border-[#D4AF37]/50 hover:text-[#F3F4F6]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <Slider label="Custom Purchase Price" value={purchase} min={4000000} max={50000000} step={100000} onChange={setPurchase} format={fmtINR} testId="calc-purchase-slider" />
            </div>
            <Slider label="Fit-Out Investment" value={fitout} min={0} max={5000000} step={50000} onChange={setFitout} format={fmtINR} testId="calc-fitout-slider" />
            <Slider label="Expected Monthly Rent" value={rent} min={20000} max={300000} step={5000} onChange={setRent} format={(v) => `₹${v.toLocaleString("en-IN")}`} testId="calc-rent-slider" />
            <Slider label="Holding Period" value={tenure} min={1} max={15} step={1} onChange={setTenure} format={(v) => `${v} yrs`} testId="calc-tenure-slider" />
            <Slider label="Expected Annual Appreciation" value={appreciation} min={4} max={15} step={0.5} onChange={setAppreciation} format={(v) => `${v}%`} testId="calc-appreciation-slider" />
          </div>

          <div className="h-fit rounded-2xl border border-[#D4AF37]/25 bg-[#121418] p-8 sm:p-10 flex flex-col">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-8">
              {metrics.map((m) => (
                <div key={m.label}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">{m.label}</div>
                  <div data-testid={m.testId} className="mt-2 font-display text-2xl sm:text-3xl font-semibold gold-text">
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[11px] text-[#6B7280] leading-relaxed border-t border-white/[0.07] pt-6">
              ROI = total rent collected over the holding period plus asset appreciation, as a share of your total
              investment. Indicative only — not a guarantee of returns or rentals. Actual outcomes depend on the unit,
              fit-out, leasing market and terms. Verify current inventory and pricing with the Homewiz.in advisory team.
            </p>
            <a
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="calc-whatsapp-cta"
              className="pt-8"
            >
              <span className="w-full py-4 rounded-full bg-[#D4AF37] text-[#0B0C0E] font-sans text-sm font-bold uppercase tracking-[0.18em] hover:bg-[#F3E5AB] hover:shadow-[0_0_36px_rgba(212,175,55,0.4)] transition-all duration-300 flex items-center justify-center gap-3">
                <MessageCircle size={16} />
                Get Current EON Options
              </span>
              <span className="block mt-3 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">
                Request verified availability on WhatsApp
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
